import { createFileRoute } from '@tanstack/react-router';
import {
  Boxes, Bell, Megaphone, BarChart3, Award, Users, Zap, ListTodo, Settings, UserCircle, Building2,
  Plus, Search, Edit, Trash2, Download, Upload, Copy, Share2, ExternalLink, X, Mail, Phone, Globe,
  MapPin, File, Folder, Image, Video, Music, Code, Calendar as CalendarIcon, Eye, EyeOff, Heart,
  Star, ChevronRight, ChevronLeft, Shield, CircleDollarSign, Clock, Target, CheckCircle,
  AlertCircle, Info, TrendingUp, TrendingDown, Linkedin, Twitter, Facebook, Instagram, Youtube
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';

export const Route = createFileRoute('/_authenticated/_layout/components/iconography')({
  component: IconographyPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function IconographyPage() {
  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Icon Library</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <p className="text-gray-900 mb-4">
                GlitchAds uses <span className="font-semibold">Lucide React</span> icons throughout the application.
                All icons are SVG-based, scalable, and accessible.
              </p>
              <div className="p-4 bg-gray-50 rounded-md space-y-2">
                <code className="text-gray-900 block">
                  import &#123; IconName &#125; from 'lucide-react';
                </code>
                <code className="text-gray-900 block">
                  &lt;IconName className="h-5 w-5" strokeWidth=&#123;2&#125; /&gt;
                </code>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Icon Stroke Width</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <p className="text-sm text-gray-700 mb-4">Match icon stroke width to text weight for visual harmony:</p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Search className="h-5 w-5 text-gray-900" strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-normal">Regular text (400) → strokeWidth=&#123;1.5&#125;</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-5 w-5 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">Medium text (500) → strokeWidth=&#123;2&#125; (Standard)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-bold">Bold text (700) → strokeWidth=&#123;2.5&#125;</p>
                  </div>
                </div>
              </div>
              <Separator className="my-6" />
              <p className="text-sm text-gray-700 mb-4">Stroke width scales inversely with icon size for consistent visual weight:</p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Search className="h-4 w-4 text-gray-900" strokeWidth={2.5} />
                  <div className="flex-1">
                    <p className="text-gray-900">Small icons (16px) → strokeWidth=&#123;2.5&#125; or &#123;2&#125;</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-5 w-5 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900">Medium icons (20px) → strokeWidth=&#123;2&#125; (Standard)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-6 w-6 text-gray-900" strokeWidth={1.75} />
                  <div className="flex-1">
                    <p className="text-gray-900">Large icons (24px) → strokeWidth=&#123;1.75&#125; or &#123;2&#125;</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-gray-900">Extra Large icons (28px) → strokeWidth=&#123;1.5&#125;</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Search className="h-12 w-12 text-gray-900" strokeWidth={1} />
                  <div className="flex-1">
                    <p className="text-gray-900">XXL icons (48px) → strokeWidth=&#123;1&#125;</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Icon Sizes</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <Search className="h-4 w-4 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold">Small (h-4 w-4) - 16px</p>
                    <p className="text-sm text-gray-700">Pairs with text-sm (12px) or text-base (15px) for inline icons, badges, and small UI elements</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-6">
                  <Search className="h-5 w-5 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold">Medium (h-5 w-5) - 20px</p>
                    <p className="text-sm text-gray-700">Pairs with text-base (15px) or text-lg (18px) for buttons, menu items, and standard UI icons</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-6">
                  <Search className="h-6 w-6 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold">Large (h-6 w-6) - 24px</p>
                    <p className="text-sm text-gray-700">Pairs with text-lg (18px) or text-xl (20px) for feature cards and prominent actions</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-6">
                  <Search className="h-7 w-7 text-gray-900" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold">Extra Large (h-7 w-7) - 28px</p>
                    <p className="text-sm text-gray-700">Pairs with text-xl (20px) or text-2xl (24px) for page headers and navigation</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-6">
                  <Search className="h-12 w-12 text-gray-900" strokeWidth={1} />
                  <div className="flex-1">
                    <p className="text-gray-900 font-semibold">XXL (h-12 w-12) - 48px</p>
                    <p className="text-sm text-gray-700">Pairs with text-2xl (24px) or text-3xl (30px) for empty states and hero sections</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Vertically Stacked Navigation Icons</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Bell className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Notifications</p>
                  <p className="text-xs text-gray-500">Bell</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Megaphone className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Campaigns</p>
                  <p className="text-xs text-gray-500">Megaphone</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <BarChart3 className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Performance</p>
                  <p className="text-xs text-gray-500">BarChart3</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Boxes className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Components</p>
                  <p className="text-xs text-gray-500">Boxes</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Award className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Brand</p>
                  <p className="text-xs text-gray-500">Award</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Users className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Competitors</p>
                  <p className="text-xs text-gray-500">Users</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Zap className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Optimizations</p>
                  <p className="text-xs text-gray-500">Zap</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <ListTodo className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Tasks</p>
                  <p className="text-xs text-gray-500">ListTodo</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Settings className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Settings</p>
                  <p className="text-xs text-gray-500">Settings</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <UserCircle className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Account</p>
                  <p className="text-xs text-gray-500">UserCircle</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Building2 className="h-7 w-7 text-gray-900" strokeWidth={1.5} />
                  <p className="text-gray-900">Organizations</p>
                  <p className="text-xs text-gray-500">Building2</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Inline Icons</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {[
                  { Icon: Plus, name: 'Plus' },
                  { Icon: Search, name: 'Search' },
                  { Icon: Edit, name: 'Edit' },
                  { Icon: Trash2, name: 'Trash2' },
                  { Icon: Download, name: 'Download' },
                  { Icon: Upload, name: 'Upload' },
                  { Icon: Copy, name: 'Copy' },
                  { Icon: Share2, name: 'Share2' },
                  { Icon: ExternalLink, name: 'ExternalLink' },
                  { Icon: X, name: 'X' },
                  { Icon: Mail, name: 'Mail' },
                  { Icon: Phone, name: 'Phone' },
                  { Icon: Globe, name: 'Globe' },
                  { Icon: MapPin, name: 'MapPin' },
                  { Icon: File, name: 'File' },
                  { Icon: Folder, name: 'Folder' },
                  { Icon: Image, name: 'Image' },
                  { Icon: Video, name: 'Video' },
                  { Icon: Music, name: 'Music' },
                  { Icon: Code, name: 'Code' },
                  { Icon: CalendarIcon, name: 'Calendar' },
                  { Icon: Eye, name: 'Eye' },
                  { Icon: EyeOff, name: 'EyeOff' },
                  { Icon: Heart, name: 'Heart' },
                  { Icon: Star, name: 'Star' },
                  { Icon: ChevronRight, name: 'ChevronRight' },
                  { Icon: ChevronLeft, name: 'ChevronLeft' },
                  { Icon: Shield, name: 'Shield' },
                  { Icon: CircleDollarSign, name: 'CircleDollarSign' },
                  { Icon: Clock, name: 'Clock' },
                  { Icon: Target, name: 'Target' },
                ].map(({ Icon, name }) => (
                  <div key={name} className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                    <Icon className="h-6 w-6 text-gray-900" strokeWidth={2} />
                    <p className="text-sm text-gray-900">{name}</p>
                  </div>
                ))}
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <CheckCircle className="h-6 w-6 text-green-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">CheckCircle</p>
                  <p className="text-xs text-gray-500">Success (filled)</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <AlertCircle className="h-6 w-6 text-amber-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">AlertCircle</p>
                  <p className="text-xs text-gray-500">Warning</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <AlertCircle className="h-6 w-6 text-red-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">AlertCircle</p>
                  <p className="text-xs text-gray-500">Error</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <Info className="h-6 w-6 text-blue-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">Info</p>
                  <p className="text-xs text-gray-500">Information</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <TrendingUp className="h-6 w-6 text-green-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">TrendingUp</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <TrendingDown className="h-6 w-6 text-red-600" strokeWidth={2} />
                  <p className="text-sm text-gray-900">TrendingDown</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Social Media Icons</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <p className="text-gray-900 mb-4">
                Social media icons with their respective brand colors for consistent representation across the platform.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#0A66C2]">
                    <Linkedin className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">LinkedIn</p>
                  <p className="text-xs text-gray-500">#0A66C2</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#1DA1F2]">
                    <Twitter className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">Twitter</p>
                  <p className="text-xs text-gray-500">#1DA1F2</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#1877F2]">
                    <Facebook className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">Facebook</p>
                  <p className="text-xs text-gray-500">#1877F2</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#E4405F]">
                    <Instagram className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">Instagram</p>
                  <p className="text-xs text-gray-500">#E4405F</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#FF0000]">
                    <Youtube className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">YouTube</p>
                  <p className="text-xs text-gray-500">#FF0000</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 hover:bg-gray-50 rounded-md">
                  <div className="h-8 w-8 rounded flex items-center justify-center bg-[#000000]">
                    <Video className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-gray-900">TikTok</p>
                  <p className="text-xs text-gray-500">#000000</p>
                  <p className="text-xs text-gray-500 italic">(Using Video icon)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Icon Colors</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center gap-6">
                <Settings className="h-7 w-7 text-gray-900" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">text-gray-900 (Dark Slate #0f172a)</p>
                  <p className="text-sm text-gray-700">Primary icon color for all standard icons</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-6">
                <Settings className="h-7 w-7 text-gray-500" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">Gray-500 (text-gray-500)</p>
                  <p className="text-sm text-gray-700">Disabled or muted icons</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-6">
                <Settings className="h-7 w-7 text-[#290BD9]" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">Primary Brand (text-[#290BD9] Cobalt)</p>
                  <p className="text-sm text-gray-700">Active or highlighted icons</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-6">
                <CheckCircle className="h-7 w-7 text-green-600" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">Success (text-green-600)</p>
                  <p className="text-sm text-gray-700">Success states</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-6">
                <AlertCircle className="h-7 w-7 text-amber-600" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">Warning (text-amber-600)</p>
                  <p className="text-sm text-gray-700">Warning states</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-center gap-6">
                <AlertCircle className="h-7 w-7 text-red-600" />
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">Error (text-red-600)</p>
                  <p className="text-sm text-gray-700">Error states</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
